/** Duplicate detection — v1's _dupKey/computeDuplicates, upgraded 2026-08-17
 * to the CANONICAL address form the server's dup radar uses (agent_runner
 * canon_addr / inbox _norm_addr — keep in sync): punctuation and unit/apt/ste
 * markers stripped, street suffixes collapsed (Street == St, Drive == Dr),
 * FLORIDA -> fl, trailing ZIP dropped. "1660 Adeline St, … FL 32763" and
 * "1660 Adeline Street, Orange City, FL" sailed past the old key and Tom saw
 * a silent double with no chip. */
import type { Lead } from "../../types";

const SUFFIX: Record<string, string> = {
  drive: "dr", avenue: "ave", street: "st", road: "rd", lane: "ln",
  court: "ct", boulevard: "blvd", place: "pl", terrace: "ter", circle: "cir",
  highway: "hwy", parkway: "pkwy", square: "sq", trail: "trl",
};

export const dupKey = (address: string): string => {
  let words = String(address ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, " ")
    .replace(/\bunit\b|\bapt\b|\bste\b/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => (w === "florida" ? "fl" : w));
  while (words.length > 3 && /^\d{4,5}$/.test(words[words.length - 1] ?? "")) words = words.slice(0, -1);
  return words.map((w) => SUFFIX[w] ?? w).join(" ");
};

export interface DupInfo {
  count: number;
  where: string; // v1 dupWhere: "source · status" of every member, joined
  /** the OTHER side(s) of the duplicate, per member (Tom 2026-08-17:
   *  "whats the other duplicate") — address + source + status. */
  others: { address: string; source: string; status: string }[];
}

/** canon address minus the house number — the street+city part. */
const streetKey = (address: string): string => {
  const w = dupKey(address).split(" ");
  return (w.length && /^\d+$/.test(w[0] ?? "") ? w.slice(1) : w).join(" ");
};

/** Masked-number duplicate: same street+city, DIFFERENT house number, and the
 * hard specs agree — sqft AND year built equal, plus price or ARV equal.
 * (Tom 2026-08-17, 3486 vs 3229 Hillmont Cir: Mark's sheet vs his own
 * InvestorLift posting with the number disguised — $310k/$515k ARV/2,101
 * sqft/1989 on both. No address matcher can see through a faked number;
 * the deal's fingerprint can.) */
const specTwin = (a: Lead, b: Lead): boolean =>
  !!(a.sqft && b.sqft && a.sqft === b.sqft
    && a.yearBuilt && b.yearBuilt && a.yearBuilt === b.yearBuilt
    && ((a.price && b.price && a.price === b.price)
      || (a.arv && b.arv && a.arv === b.arv)));

/** Map lead.id → dup info for every lead in a >1 group. Statuses come from
 * the caller (v1 shows "source · stage"). */
export function computeDups(leads: Lead[], statusOfLead: (l: Lead) => string): Map<string, DupInfo> {
  const byKey = new Map<string, Lead[]>();
  for (const l of leads) {
    const k = dupKey(l.address);
    if (!k) continue;
    const g = byKey.get(k);
    if (g) g.push(l);
    else byKey.set(k, [l]);
  }
  // groups as an array so fingerprint links can MERGE address-groups
  const groupOf = new Map<string, number>();   // dupKey -> group index
  const groups: Lead[][] = [];
  for (const [k, g] of byKey) {
    groupOf.set(k, groups.length);
    groups.push([...g]);
  }
  // masked-number pass: same street, different house number, specs agree
  const byStreet = new Map<string, string[]>(); // streetKey -> dupKeys
  for (const k of byKey.keys()) {
    const raw = byKey.get(k);
    const sk = raw && raw[0] ? streetKey(raw[0].address) : "";
    if (!sk) continue;
    const arr = byStreet.get(sk);
    if (arr) arr.push(k);
    else byStreet.set(sk, [k]);
  }
  for (const keys of byStreet.values()) {
    if (keys.length < 2) continue;
    for (let i = 0; i < keys.length; i++) {
      for (let j = i + 1; j < keys.length; j++) {
        const gi = groupOf.get(keys[i] ?? "") ?? -1;
        const gj = groupOf.get(keys[j] ?? "") ?? -1;
        if (gi < 0 || gj < 0 || gi === gj) continue;
        const match = (groups[gi] ?? []).some((a) => (groups[gj] ?? []).some((b) => specTwin(a, b)));
        if (!match) continue;
        const merged = [...(groups[gi] ?? []), ...(groups[gj] ?? [])];
        groups[gi] = merged;
        groups[gj] = [];
        for (const [k2, idx] of groupOf) if (idx === gj) groupOf.set(k2, gi);
      }
    }
  }
  const out = new Map<string, DupInfo>();
  for (const g of groups) {
    if (g.length < 2) continue;
    const where = g.map((l) => `${l.source} · ${statusOfLead(l)}`).join(", ");
    for (const l of g) {
      const others = g.filter((o) => o.id !== l.id)
        .map((o) => ({ address: o.address, source: o.source, status: statusOfLead(o) }));
      out.set(l.id, { count: g.length, where, others });
    }
  }
  return out;
}
